import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext, ThemeContext } from '../Context';
import GetUserAvatar from '../tool/GetUserAvatar';
import { 
  CardContent, 
  Typography, 
  Box,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Slider
} from '@mui/material';

// Génération des couleurs pour la barre horizontale
const generateColorSpectrum = () => {
  const colors = [];
  
  // Génération des couleurs du spectre (sans blanc, noir ou gris)
  for (let h = 0; h < 360; h += 3) {  // Pas de 3 degrés pour une barre fluide
    colors.push(`hsl(${h}, 100%, 50%)`);
  }
  
  return colors;
};

const COLOR_SPECTRUM = generateColorSpectrum();

const ColorBar = ({ onColorSelect, selectedColor, isDark }) => {
  const [hoveredColor, setHoveredColor] = useState(null);

  // Trouve l'index de la couleur la plus proche dans le spectre
  const getClosestColorIndex = (targetColor) => {
    // Simple implémentation pour trouver une correspondance approximative
    // Dans un cas réel, on utiliserait une conversion plus précise
    return COLOR_SPECTRUM.findIndex(color => color === targetColor) || 0;
  };

  return (
    <Box 
      sx={{ 
        position: 'relative',
        width: '100%',
        height: '40px',
        borderRadius: '4px',
        overflow: 'hidden',
        cursor: 'pointer',
        border: isDark 
          ? '1px solid rgba(144, 202, 249, 0.3)' 
          : '1px solid #ccc',
        mb: 2
      }}
    >
      <Box 
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(to right, ${COLOR_SPECTRUM.join(', ')})`,
        }}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const ratio = x / rect.width;
          const colorIndex = Math.min(Math.floor(ratio * COLOR_SPECTRUM.length), COLOR_SPECTRUM.length - 1);
          onColorSelect(COLOR_SPECTRUM[colorIndex]);
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const ratio = x / rect.width;
          const colorIndex = Math.min(Math.floor(ratio * COLOR_SPECTRUM.length), COLOR_SPECTRUM.length - 1);
          setHoveredColor(COLOR_SPECTRUM[colorIndex]);
        }}
        onMouseLeave={() => setHoveredColor(null)}
      />
      
      {/* Indicateur de couleur sélectionnée */}
      {selectedColor && (
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            left: `${(getClosestColorIndex(selectedColor) / COLOR_SPECTRUM.length) * 100}%`,
            width: '4px',
            height: '100%',
            backgroundColor: 'white',
            border: '1px solid black',
            transform: 'translateX(-2px)',
            zIndex: 2
          }}
        />
      )}
      
      {/* Prévisualisation au survol */}
      {hoveredColor && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: hoveredColor,
            width: '20px',
            height: '20px',
            border: isDark 
              ? '1px solid rgba(144, 202, 249, 0.3)' 
              : '1px solid #ccc',
            borderRadius: '50%',
            zIndex: 2
          }}
        />
      )}
    </Box>
  );
};

const AccountSettings = () => {
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedColor, setSelectedColor] = useState(COLOR_SPECTRUM[0]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Styles adaptatifs pour le thème
  const isDark = theme === 'dark';

  // Récupération des informations de l'utilisateur
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const response = await axios.get('/api/user', config);
        const currentUser = response.data.find(u => u.matricule === user);
        
        if (currentUser) {
          setUserInfo(currentUser);
          // Utilisation de la couleur actuelle ou par défaut
          setSelectedColor(currentUser.nicknameColor || COLOR_SPECTRUM[0]);
        }
      } catch (err) {
        setError('Erreur lors de la récupération des informations utilisateur');
        console.error(err);
      }
    };

    if (user) {
      fetchUserInfo();
    }
  }, [user]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = sessionStorage.getItem('authToken');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(
        `/api/user/${user}/settings`,
        { nicknameColor: selectedColor },
        config
      );

      // Met à jour localement pour refléter immédiatement le changement
      setUserInfo(prev => prev ? { ...prev, nicknameColor: selectedColor } : prev);
      // Notifie l'application pour que les autres composants se mettent à jour
      window.dispatchEvent(new CustomEvent('nicknameColorUpdated', {
        detail: { matricule: user, color: selectedColor }
      }));

      setSuccess('Couleur mise à jour avec succès !');
    } catch (err) {
      setError('Erreur lors de la mise à jour de la couleur');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="warning">Veuillez vous connecter pour accéder aux paramètres.</Alert>
      </Box>
    );
  }

  if (!userInfo) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Box display="flex" justifyContent="center" m={2}>
          <CircularProgress sx={{ color: isDark ? '#90caf9' : 'inherit' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component={Paper}
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: isDark ? '#233044' : '#ffffff',
        color: isDark ? '#fff' : 'inherit',
        boxShadow: isDark 
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      <CardContent>
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom
          sx={{ color: isDark ? '#90caf9' : 'inherit' }}
        >
          Couleur de mon nom
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body2" 
            gutterBottom
            sx={{ color: isDark ? '#b0bec5' : 'inherit' }}
          >
            Sélectionnez votre couleur :
          </Typography>
          
          <ColorBar 
            onColorSelect={setSelectedColor}
            selectedColor={selectedColor}
            isDark={isDark}
          />
          
          {/* Affichage de la couleur sélectionnée */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 3 
            }}
          >
            <Typography 
              variant="body2"
              sx={{ color: isDark ? '#b0bec5' : 'inherit' }}
            >
              Couleur sélectionnée :
            </Typography>
            <Box 
              sx={{ 
                width: 40, 
                height: 40, 
                bgcolor: selectedColor, 
                border: isDark 
                  ? '1px solid rgba(144, 202, 249, 0.3)' 
                  : '1px solid #ccc',
                borderRadius: 1 
              }} 
            />
            <Typography 
              variant="body2"
              sx={{ color: isDark ? '#b0bec5' : 'inherit' }}
            >
              Prévisualisation :
            </Typography>
            <Box 
              sx={{ 
                p: 2,
                height: 40,
                border: isDark 
                  ? '1px solid rgba(144, 202, 249, 0.3)' 
                  : '1px solid #ccc',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'inherit'
              }} 
            >
              <Typography>
                <GetUserAvatar 
                  matricule={userInfo.matricule} 
                  defaultValue="..." 
                  nicknameColorOverride={selectedColor}
                />
              </Typography>
            </Box>
          </Box>

          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={loading}
            color="primary"
            fullWidth
            sx={{
              bgcolor: isDark ? '#90caf9' : 'inherit',
              color: isDark ? '#1a253c' : 'inherit',
              '&:hover': {
                bgcolor: isDark ? '#64b5f6' : 'inherit'
              },
              '&:disabled': {
                bgcolor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'inherit',
                color: isDark ? 'rgba(26, 37, 60, 0.6)' : 'inherit'
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: isDark ? '#1a253c' : 'inherit' }} /> : 'Sauvegarder'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </CardContent>
    </Box>
  );
};

export default AccountSettings;